import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { inr, convertFileToBase64 } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Ban, Trash2, ChevronDown, Check, UserCheck, Image as ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function CustomDropdown({ value, options, onChange, placeholder = "Select", className = "", disabled = false }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => String(o.value) === String(value));

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-white/20 bg-black/70 text-white text-sm outline-none focus:border-hgold hover:border-hgold/70 transition-all ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
      >
        <span className={`truncate ${selectedOption ? "text-white font-medium" : "text-white/40"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={16} className={`transform transition-transform duration-300 text-amber-400 shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -5 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 mt-2 rounded-2xl border-2 border-amber-400/80 bg-[#0c0c0c]/98 backdrop-blur-xl shadow-2xl shadow-yellow-950/50 z-50 p-2"
          >
            <div className="max-h-56 overflow-y-auto pr-1.5 space-y-1">
              {options.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3.5 py-2.5 text-xs font-bold tracking-wide flex items-center justify-between rounded-xl transition-colors ${
                      isSelected
                        ? "bg-amber-500/25 text-amber-300 border-l-4 border-amber-400"
                        : "text-white/80 hover:bg-white/10 hover:text-amber-300"
                    }`}
                  >
                    <span className="truncate pr-2">{opt.label}</span>
                    {isSelected && <Check size={15} className="text-amber-400 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const emptyForm = {
  display_name: "", branch_id: "", experience_years: "",
  body_type: "athletic", role_preference: "versatile",
  category: [], phone: "", email: "", gov_id: "", bank_details: "",
  home_service_rate: "", spa_service_rate: "",
  photos: [], bio: "", status: "active",
};

export default function AdminTherapists() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const r = await api.get("/therapists" + (user.role === "branch_admin" ? `?branch_id=${user.branch_id}` : ""));
    setList(r.data);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/branches"),
      api.get("/therapists" + (user.role === "branch_admin" ? `?branch_id=${user.branch_id}` : ""))
    ]).then(([bRes, tRes]) => {
      setBranches(bRes.data);
      setList(tRes.data);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [user.branch_id, user.role]);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, branch_id: user.branch_id || "" });
    setOpen(true);
  };

  const startEdit = (t) => {
    setEditing(t.id);
    setForm({ ...emptyForm, ...t });
    setOpen(true);
  };

  const [saving, setSaving] = useState(false);

  const save = async () => {
    try {
      setSaving(true);
      const payload = { ...form, experience_years: Number(form.experience_years), home_service_rate: Number(form.home_service_rate), spa_service_rate: Number(form.spa_service_rate) };
      if (editing) await api.put(`/admin/therapists/${editing}`, payload);
      else await api.post("/admin/therapists", payload);
      toast.success(editing ? "Therapist updated" : "Therapist added successfully");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save therapist");
    } finally {
      setSaving(false);
    }
  };

  const blacklist = async (t) => {
    if (user.role !== "master_admin") return toast.error("Master admin only");
    const reason = prompt(`Blacklist reason for ${t.display_name}?`);
    if (!reason) return;
    try {
      await api.post("/admin/blacklist", { therapist_id: t.id, reason });
      toast.success("Blacklisted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to blacklist"); }
  };

  const del = async (id) => {
    if (!confirm("Are you sure you want to delete this therapist?")) return;
    try {
      await api.delete(`/admin/therapists/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-emerald-400 font-bold uppercase tracking-widest animate-pulse">Loading Therapists...</div>
      </div>
    );
  }

  const branchOptions = branches.map((b) => ({ label: b.name, value: b.id }));
  const statusOptions = [
    { label: "Active", value: "active" },
    { label: "On Leave", value: "on_leave" },
    { label: "Suspended", value: "suspended" },
  ];

  return (
    <div data-testid="admin-therapists" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-white/10 pb-6"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1">TEAM & STAFF</div>
          <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            Therapists
          </h1>
        </div>

        <button
          onClick={startCreate}
          className="gold-btn rounded-xl py-3 px-6 font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40 flex items-center gap-2"
          data-testid="therapist-add-btn"
        >
          <Plus size={16} /> Add Therapist
        </button>
      </motion.div>

      {/* Cards Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        {list.map((t) => {
          const branch = branches.find((b) => b.id === t.branch_id);
          return (
            <div
              key={t.id}
              className="rounded-2xl border border-amber-400/70 overflow-hidden bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 hover:scale-[1.02] transition-all flex flex-col justify-between"
              data-testid={`therapist-card-admin-${t.id}`}
            >
              <div>
                <div className="aspect-[3/4] relative overflow-hidden bg-black/60">
                  <img src={t.photos?.[0] || "https://images.pexels.com/photos/38045116/pexels-photo-38045116.jpeg"} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" />
                  <div className="absolute bottom-3 left-4 right-4">
                    <span className={`text-[10px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full border shadow-md inline-block ${
                      t.status === "active" ? "bg-emerald-950/90 text-emerald-400 border-emerald-400/50" :
                      t.status === "blacklisted" ? "bg-red-950/90 text-red-400 border-red-500/50" :
                      "bg-amber-950/90 text-amber-300 border-amber-400/50"
                    }`}>
                      {t.status}
                    </span>
                  </div>
                </div>

                <div className="p-5 space-y-2">
                  <h3 className="font-heading text-2xl font-bold text-white">{t.display_name}</h3>
                  <div className="text-xs text-amber-300/80 font-semibold tracking-wide">
                    {branch?.name || "All Branches"} · <span className="text-white/60">{t.experience_years} yrs exp</span>
                  </div>

                  <div className="text-sm font-bold text-emerald-400 pt-1">
                    Spa: {inr(t.spa_service_rate)} · Home: {inr(t.home_service_rate)}
                  </div>
                </div>
              </div>

              <div className="p-5 pt-0 flex items-center gap-2">
                <button
                  onClick={() => startEdit(t)}
                  className="flex-1 py-2.5 rounded-xl border border-amber-400/60 bg-amber-950/30 hover:bg-amber-500/20 text-amber-300 text-xs uppercase font-extrabold tracking-wider transition-all"
                >
                  Edit
                </button>
                <div className="flex gap-1.5 shrink-0">
                  {user.role === "master_admin" && t.status !== "blacklisted" && (
                    <button
                      onClick={() => blacklist(t)}
                      title="Blacklist"
                      className="border border-red-500/40 bg-red-950/20 hover:bg-red-900/40 text-red-300 p-2.5 rounded-xl transition-all shadow-sm shadow-red-950/30"
                    >
                      <Ban size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => del(t.id)}
                    title="Delete"
                    className="border border-red-500/40 bg-red-950/20 hover:bg-red-900/40 text-red-300 p-2.5 rounded-xl transition-all shadow-sm shadow-red-950/30"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="col-span-full text-center py-16 text-white/50 bg-hpaper border-2 border-white/10 rounded-2xl font-medium">
            No therapists found.
          </div>
        )}
      </motion.div>

      {/* Dialog Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0c0c0c] border border-amber-400/80 rounded-2xl text-white max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl p-6 lg:p-8">
          <DialogHeader className="border-b border-white/10 pb-4">
            <DialogTitle className="font-heading text-3xl text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
              {editing ? "Edit Therapist Details" : "Add New Therapist"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Full Name / Display Name</label>
              <input
                placeholder="Enter therapist full name"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                data-testid="ther-form-name"
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Assigned Branch</label>
                <CustomDropdown
                  disabled={user.role === "branch_admin"}
                  value={form.branch_id}
                  options={branchOptions}
                  onChange={(val) => setForm({ ...form, branch_id: val })}
                  placeholder="Select branch"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Experience (Years)</label>
                <input
                  type="number"
                  placeholder="e.g. 3"
                  value={form.experience_years}
                  onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Phone Number</label>
                <input
                  placeholder="Enter 10-digit phone number"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Email Address</label>
                <input
                  placeholder="Enter email address"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Spa Service Rate (₹)</label>
                <input
                  type="number"
                  placeholder="e.g. 999"
                  value={form.spa_service_rate}
                  onChange={(e) => setForm({ ...form, spa_service_rate: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-bold text-emerald-400"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Home Service Rate (₹)</label>
                <input
                  type="number"
                  placeholder="e.g. 1299"
                  value={form.home_service_rate}
                  onChange={(e) => setForm({ ...form, home_service_rate: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-bold text-amber-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Photo Upload */}
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Therapist Profile Photo</label>
                {form.photos?.[0] ? (
                  <div className="flex items-center gap-3">
                    <div className="w-28 h-36 rounded-xl border-2 border-amber-400/80 overflow-hidden bg-black flex items-center justify-center shadow-md">
                      <img src={form.photos[0]} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, photos: [] })}
                      className="text-xs border border-red-500/40 bg-red-950/20 text-red-300 hover:bg-red-900/40 px-3 py-2 rounded-xl font-bold uppercase tracking-wider transition-all"
                    >
                      Remove Photo
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-36 rounded-2xl border-2 border-dashed border-white/20 hover:border-hgold cursor-pointer bg-black/40 hover:bg-black/60 transition-all p-4 text-center">
                    <ImageIcon className="w-7 h-7 text-amber-400 mb-1" />
                    <span className="text-xs font-bold text-white">Click to upload photo</span>
                    <span className="text-[10px] text-white/50 mt-1">Supports JPG, PNG, WEBP</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            const base64 = await convertFileToBase64(file);
                            setForm({ ...form, photos: [base64] });
                          } catch {
                            toast.error("Failed to read file");
                          }
                        }
                      }}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Bio & Status */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-white/90 block mb-1.5">Active Status</label>
                  <CustomDropdown
                    value={form.status}
                    options={statusOptions}
                    onChange={(val) => setForm({ ...form, status: val })}
                    placeholder="Status"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-white/90 block mb-1.5">Short Bio</label>
                  <textarea
                    rows="3"
                    placeholder="Enter short biography..."
                    value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 outline-none rounded-xl resize-none text-sm text-white transition-all"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={save}
              disabled={saving}
              className="gold-btn w-full justify-center py-4 rounded-xl font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40 flex items-center gap-2 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="ther-form-submit"
            >
              {saving ? (
                <>
                  <span className="premium-spinner border-t-transparent w-4 h-4 shrink-0"></span>
                  <span>{editing ? "Saving Changes..." : "Adding Therapist..."}</span>
                </>
              ) : (
                <>
                  <UserCheck size={16} />
                  <span>{editing ? "Save Changes" : "Add Therapist"}</span>
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
